import { useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { ExportButton } from '@/components/common/ExportButton';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { exportMpApprovals, mpApprovalsInfiniteQueryOptions } from '../api/marketPromotion.api';
import { DonationApprovalFilterPanel } from '../components/DonationApprovalFilterPanel';
import { DonationFormDialog, type DonationAction } from '../components/DonationFormDialog';
import { getMpApprovalColumns } from '../components/MpColumns';
import { RecordCallDialog } from '../components/RecordCallDialog';
import { ViewDonationDialog } from '../components/ViewDonationDialog';
import { DONATION_UPCOMING_SEARCH } from '../constants/marketPromotion.constants';
import { useMpApprovalFilters } from '../hooks/useMpApprovalFilters';
import type { MpApprovalRow } from '../types/marketPromotion.types';

interface MpApprovalPageProps {
  routeId: 'approvals' | 'mis';
  title: string;
  showCall: boolean;
}

export function MpApprovalPage({ routeId, title, showCall }: MpApprovalPageProps) {
  const controller = useMpApprovalFilters(routeId);
  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    mpApprovalsInfiniteQueryOptions(controller.params, routeId === 'mis'),
  );

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editing, setEditing] = useState<{
    donation: MpApprovalRow;
    action: DonationAction;
  } | null>(null);
  const [viewing, setViewing] = useState<MpApprovalRow | null>(null);
  const [calling, setCalling] = useState<MpApprovalRow | null>(null);

  const columns = getMpApprovalColumns(
    {
      onRecommend: (donation) => setEditing({ donation, action: 'recommend' }),
      onDecide: (donation) => setEditing({ donation, action: 'decide' }),
      onView: setViewing,
      ...(showCall
        ? {
            onAmend: (donation: MpApprovalRow) => setEditing({ donation, action: 'amend' }),
            onCall: setCalling,
          }
        : {}),
    },
    showCall,
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        search={DONATION_UPCOMING_SEARCH}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              onExport={() => exportMpApprovals(controller.params)}
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <DonationApprovalFilterPanel filters={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the queue"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="instituteName"
          emptyMessage={
            showCall
              ? 'Nobody has called about a donation in this period'
              : 'No cash donation is waiting'
          }
          {...loadMoreProps}
        />
      )}

      <DonationFormDialog
        donation={editing?.donation ?? null}
        action={editing?.action ?? 'decide'}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />

      <ViewDonationDialog
        donation={viewing}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
      />

      <RecordCallDialog
        donation={calling}
        onOpenChange={(open) => {
          if (!open) setCalling(null);
        }}
      />
    </div>
  );
}
